import _ from 'lodash'
import ApiClient from 'apis/ApiClient'
import { logAmplitudeEvent, setAmplitudeProfile } from 'utils/amplitudeInit'
import { postNativeMessage } from 'utils/basic'
import { getAPICachePath } from './formatUrl'
import { getPartnerId } from 'utils/authLocalStorage'
import { getWWAge, getWWGender } from 'utils/wwLocalStorage'
const apiClient = new ApiClient()

export function getCategoryListPromise() {
  return apiClient
    .get(`/workout/category`, {
      cacheTtl: 60 * 60 * 1000,
    })
    .then((resp) => Promise.resolve(resp.data))
}

export function getWorkoutDetailPromiseV2(workoutId) {
  return apiClient
    .get(`/workout/detail/${workoutId}`, {
      version: 'v2',
    })
    .then((resp) => Promise.resolve(resp.data))
}

export function getWorkoutListByTypePromise(options) {
  const cacheTtl = options?.type === 'favorites' ? 0 : 10 * 60 * 1000
  return apiClient
    .get(`/workout/browse_title`, {
      version: 'v2',
      cacheTtl: cacheTtl,
      params: {
        ...options,
      },
    })
    .then((resp) => Promise.resolve(resp.data))
}

export function getPartnerGoalMapPromise() {
  return apiClient
    .get(`/partner/plan/goal`)
    .then((resp) => Promise.resolve(resp.data))
}

export function collectWorkoutPromise(workoutId, collection, workout, source) {
  logAmplitudeEvent(
    collection ? 'Workout: Favorite Added' : 'Workout: Favorite Deleted',
    {
      ID: workout.workoutId,
      Name: workout.workoutName,
      Trainer: workout.trainerName,
      Source: source,
    }
  )
  postNativeMessage({
    event: collection
      ? 'fiton_workout_favorite_add'
      : 'fiton_workout_favorite_remove',
    attributes: {
      id: workout.workoutId,
      name: workout.workoutName,
    },
  })
  return apiClient.put('/workout/collection', {
    body: {
      workoutId,
      collection,
    },
  })
}

export function changeStatusPromise(workoutId, payload, workout) {
  return apiClient.put('/workout/change_status', {
    body: {
      workoutId,
      ...payload,
    },
  })
}

export function joinChallengePromise(challengeId, join) {
  return apiClient.post('/challenge/join', {
    body: {
      challengeId,
      join,
    },
  })
}

export function restartPromise(workoutId) {
  return apiClient.put(`/workout/reset/${workoutId}`)
}

export const getWorkoutSummaryPromise = (type) => {
  return apiClient
    .get('/user/workout/summary', { params: { type: type || 'today' } })
    .then((resp) => {
      setAmplitudeProfile({ completedWorkout: _.get(resp, 'data.completed') })
      return Promise.resolve(resp.data)
    })
}

export const getWorkoutHistoryPromise = (
  type,
  friendId,
  lastPageTime,
  page,
  size
) => {
  const params = { page: page, size: size }
  if (lastPageTime > 0) {
    params.lastPageTime = lastPageTime
  }
  if (type) {
    params.type = type
  }
  if (friendId > 0) {
    params.friendId = friendId
  }
  return apiClient
    .get('/workout/history', {
      params: params,
      version: 'v2',
    })
    .then((resp) => {
      return Promise.resolve(_.get(resp, 'data.workouts', []))
    })
}

export const getWorkoutBrowsePart2Promise = (type) => {
  return apiClient
    .get('/workout/browse_part2', {
      version: 'v2',
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const getWorkoutBrowsePart3Promise = (type) => {
  return apiClient
    .get('/partner/browse_part3')
    .then((resp) => Promise.resolve(resp.data))
}

export function postWorkoutRatingPromise(options) {
  return apiClient.post('/workout/rate', {
    body: options,
  })
}

export const getChallengeDetailPromise = (challengeId) => {
  return apiClient
    .get(`/challenge/${challengeId}`, { version: 'v2' })
    .then((resp) => {
      return Promise.resolve(_.get(resp, 'data', {}))
    })
}

export function getResourceAuthPromise(versionMap = {}) {
  return apiClient
    .get(getAPICachePath(`/partner/${getPartnerId()}/workout/resources`))
    .then((resp) => Promise.resolve(_.get(resp, 'data', [])))
}

export function updateActivityPromise(recordId, params = {}) {
  return apiClient
    .put(`/activity/${recordId}`, {
      body: { ...params },
    })
    .then((resp) => Promise.resolve(resp.data))
}

export function deleteActivityPromise(recordId) {
  return apiClient
    .delete(`/activity/${recordId}`)
    .then((resp) => Promise.resolve(resp.data))
}

export function getCategoryPromise() {
  return apiClient
    .get(`/activity/category`, {
      version: 'v1',
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const getChallengeList = () => {
  return apiClient.get('/challenges').then((resp) => Promise.resolve(resp.data))
}

export const getChallengeCompleteStatusPromise = (workoutId) => {
  return apiClient
    .get('/challenges/status/by-workout', { params: { workoutId: workoutId } })
    .then((resp) => Promise.resolve(resp.data))
}

export const searchWorkoutPromise = (params) => {
  return apiClient
    .post('/workout/browse/search', {
      body: { ...params },
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const getSearchFilterDataPromise = () => {
  return apiClient
    .get('workout/browse/search-data', { cacheTtl: 60 * 60 * 1000 })
    .then((resp) => Promise.resolve(resp.data))
}
export const getBasicDataPromise = () => {
  return apiClient
    .get(`/util/basic-data`, {
      params: _.omitBy(
        {
          age: getWWAge(),
          gender: getWWGender(),
          partnerId: getPartnerId(),
          types: ['workoutSubcategories'],
        },
        _.isNil
      ),
      cacheTtl: 60 * 60 * 1000,
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const getWorkoutCalorieByRecordIdsPromise = (recordIds) => {
  const ids = _.join(recordIds, ',')
  return apiClient
    .get('/workout/info/total', {
      params: {
        recordIds: `[${ids}]`,
      },
    })
    .then((resp) => Promise.resolve(resp?.data || {}))
}

export const challengeInteractPromise = (challengeId, workoutId) => {
  return apiClient
    .post('/challenges/interact', {
      body: { challengeId, workoutId },
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const nextUpWorkoutPromise = ({ guid, planId }) => {
  const body = { guid, fromWeb: true }
  if (planId) {
    body.planId = planId
  }
  return apiClient
    .post('/partner/api/workout/nextup', {
      params: body,
      partnerPath: true,
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const partnerNextUpWorkoutPromise = () => {
  return apiClient
    .get('/partner/user/personalized-workout', {
      cacheTtl: 5 * 60 * 1000,
    })
    .then((resp) => Promise.resolve(resp.data))
}

export const partnerLatestWorkoutPromise = () => {
  return apiClient
    .get('/partner/user/latest-workout')
    .then((resp) => Promise.resolve(resp.data))
}

export const getPlanWorkoutPromise = ({ planId }) => {
  return apiClient
    .get(`/partner/plan/workout/${planId}`)
    .then((resp) => Promise.resolve(resp.data))
}

export const getPlanWorkoutWithoutPlanIdPromise = () => {
  return apiClient
    .get(`/partner/plan/workout`)
    .then((resp) => Promise.resolve(resp.data))
}
